import './css/common.css'
import './css/app.css'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { AlertTriangle, Copy, Eye, RefreshCw, Search, X } from 'lucide-react';
import { change_Current_Location, changeHeaderTitle } from '../store';
import List from './component/list/list';
import { LoadingModal, MessageModal } from './component/modal/modal';
import OnuManageTabs from './component/onu/OnuManageTabs';
import PageHelp from './component/help/PageHelp';
import ManualDownloadLink from './component/manual/ManualDownloadLink';

const API_BASE = "/api/noti-bypass-send-logs";
const MAX_VISIBLE_TABLE_ROWS = 20;
const TABLE_ROW_HEIGHT = 32;
const TABLE_HEADER_HEIGHT = 44;
const RESENDABLE_STATUSES = ["RETRYING", "FAILED", "MANUAL_FAILED"];
const SUCCESS_STATUSES = ["SUCCESS", "MANUAL_SUCCESS"];
const FAILURE_STATUSES = ["RETRYING", "FAILED", "MANUAL_FAILED"];
const HIDDEN_GUIDE_MESSAGES = [
    "이 화면은 payblue-buffer의 바이패스 전송 이력을 조회합니다.",
    "수동 재전송은 원본 payload를 그대로 외부 endpoint에 다시 전송합니다.",
    "확인이 필요한 바이패스 전송 이력을 조회합니다.",
    "수동 재전송은 거래 정보를 확인한 뒤 실행해주세요.",
];

const STATUS_LABELS = {
    PENDING: "전송 대기",
    SUCCESS: "자동 성공",
    RETRYING: "재시도 대기",
    FAILED: "자동 실패",
    MANUAL_SUCCESS: "수동 성공",
    MANUAL_FAILED: "수동 실패",
};

function getTodayDateString() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const date = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${date}`;
}

function createInitialFilters() {
    const today = getTodayDateString();
    return {
        statuses: [],
        vendor: "",
        mcht_id: "",
        app_no: "",
        created_from: today,
        created_to: today,
        limit: 50,
    };
}

function getDateStringWithOffset(offsetDays) {
    const target = new Date();
    target.setDate(target.getDate() + offsetDays);
    const year = target.getFullYear();
    const month = String(target.getMonth() + 1).padStart(2, "0");
    const date = String(target.getDate()).padStart(2, "0");
    return year + "-" + month + "-" + date;
}

function getStatusLabel(status) {
    return STATUS_LABELS[status] || status || "-";
}

function isSuccess(responseData) {
    return responseData?.code === 1000;
}

function getErrorMessage(responseData, fallback) {
    return responseData?.msg || fallback;
}

function formatJson(value) {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
        return String(value);
    }
}


function formatKstDateTime(value) {
    if (value === null || value === undefined || value === "") return "-";
    const raw = String(value).trim();
    if (!raw) return "-";

    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
    const parsed = new Date(hasTimezone ? normalized : normalized + "Z");
    if (Number.isNaN(parsed.getTime())) return raw;

    const kst = new Date(parsed.getTime() + (9 * 60 * 60 * 1000));
    const pad = (num) => String(num).padStart(2, "0");
    return [
        kst.getUTCFullYear(),
        pad(kst.getUTCMonth() + 1),
        pad(kst.getUTCDate()),
    ].join("-") + " " + [
        pad(kst.getUTCHours()),
        pad(kst.getUTCMinutes()),
        pad(kst.getUTCSeconds()),
    ].join(":");
}

function NotiBypassSendLogs() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const MyData = useSelector((state) => state.MyData);
    const anthScreen = useSelector((state) => state.anthScreen);
    const canManualResend =
        MyData?.role === "SUPER" &&
        anthScreen.includes("오앤유 관리");

    const [message, setMessage] = useState({});
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [meta, setMeta] = useState({
        statuses: ["PENDING", "SUCCESS", "RETRYING", "FAILED", "MANUAL_SUCCESS", "MANUAL_FAILED"],
        default_statuses: [],
        guide_messages: [],
    });
    const [filters, setFilters] = useState(() => createInitialFilters());
    const [appliedFilters, setAppliedFilters] = useState(() => createInitialFilters());
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPage, setTotalPage] = useState(1);
    const [detailLog, setDetailLog] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [resendTarget, setResendTarget] = useState(null);
    const [resendMemo, setResendMemo] = useState("");
    const [resendConfirmed, setResendConfirmed] = useState(false);
    const [resending, setResending] = useState(false);


    const visibleGuideMessages = useMemo(() => (
        (meta?.guide_messages?.length ? meta.guide_messages : [])
            .filter((text) => !HIDDEN_GUIDE_MESSAGES.includes(text))
    ), [meta]);

    const fetchMeta = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_API + API_BASE + "/meta", {
                withCredentials: true,
            });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const data = tmp?.data ?? {};
                setMeta((prev) => ({
                    ...prev,
                    ...data,
                    default_statuses: data.default_statuses ?? [],
                }));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        setListLoading(true);
        try {
            const params = {
                page,
                limit: appliedFilters.limit,
            };
            params.status = appliedFilters.statuses?.length ? appliedFilters.statuses.join(",") : "all";
            ["vendor", "mcht_id", "app_no", "created_from", "created_to"].forEach((key) => {
                if (appliedFilters[key]) params[key] = appliedFilters[key];
            });

            const response = await axios.get(process.env.REACT_APP_API + API_BASE, {
                withCredentials: true,
                params,
            });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const data = tmp?.data ?? {};
                setLogs(data.logs ?? []);
                setTotal(data.total ?? 0);
                setTotalPage(data.totalPage ?? 1);
                setPage(data.currentPage ?? page);
            } else if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인이 필요합니다.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, "노티 바이패스 전송 이력 조회에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: "노티 바이패스 전송 이력 조회 중 오류가 발생했습니다." });
        } finally {
            setLoading(false);
            setListLoading(false);
        }
    }, [appliedFilters, navigate, page]);


    useEffect(() => {
        dispatch(change_Current_Location("오앤유 관리"));
        dispatch(changeHeaderTitle({
            mainTitle: "관리자 메뉴",
            subTitle: "오앤유 관리",
        }));
        fetchMeta();
    }, [dispatch, fetchMeta]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleStatusFilterChange = (value) => {
        const statuses = value === "all" ? [] : value.split(",").filter(Boolean);
        handleFilterChange("statuses", statuses);
    };


    const setDatePreset = (preset) => {
        const today = getTodayDateString();
        const range = {
            today: [today, today],
            week: [getDateStringWithOffset(-6), today],
            month: [getDateStringWithOffset(-29), today],
            all: ["", ""],
        }[preset] || [today, today];
        setFilters((prev) => ({
            ...prev,
            created_from: range[0],
            created_to: range[1],
        }));
    };

    const submitSearch = () => {
        setPage(1);
        setAppliedFilters(filters);
    };

    const resetSearch = () => {
        const nextFilters = createInitialFilters();
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setPage(1);
    };

    const readLogDetail = async (id) => {
        setDetailLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API}${API_BASE}/${id}`, {
                withCredentials: true,
            });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                return tmp?.data?.log ?? tmp?.data ?? null;
            }
            if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인이 필요합니다.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, "상세 조회에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: "상세 조회 중 오류가 발생했습니다." });
        } finally {
            setDetailLoading(false);
        }
        return null;
    };

    const openDetailModal = async (row) => {
        const log = await readLogDetail(row.id);
        if (log) setDetailLog(log);
    };

    const openResendModal = async (row) => {
        if (!canManualResend) {
            setMessage({ msg: "수동 재전송 권한이 없습니다." });
            return;
        }
        const log = await readLogDetail(row.id);
        if (!log) return;
        setResendTarget(log);
        setResendMemo("");
        setResendConfirmed(false);
    };

    const copyText = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text || "");
            setMessage({ msg: `${label}을 복사했습니다.` });
        } catch (error) {
            console.error(error);
            setMessage({ msg: "복사에 실패했습니다." });
        }
    };

    const manualResend = async () => {
        if (!resendTarget || !resendConfirmed || resending) return;
        setResending(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API}${API_BASE}/${resendTarget.id}/manual-resend`,
                { confirm: true, memo: resendMemo },
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const log = tmp?.data?.log ?? tmp?.data ?? {};
                setResendTarget(null);
                fetchLogs();
                if (log.status === "MANUAL_FAILED") {
                    setMessage({ msg: log.last_error ? `수동 재전송 실패: ${log.last_error}` : "수동 재전송에 실패했습니다." });
                } else {
                    setMessage({ msg: tmp?.msg || "수동 재전송이 완료되었습니다." });
                }
            } else if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인이 필요합니다.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, "수동 재전송에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: "수동 재전송 중 오류가 발생했습니다." });
        } finally {
            setResending(false);
        }
    };

    const tableHeaderData = [
        { no: 1, text: <HeaderCell label="번호" />, width: "80px", textAlign: "center" },
        {
            no: 2,
            text: (
                <HeaderControl>
                    <input
                        value={filters.vendor}
                        onChange={(event) => handleFilterChange("vendor", event.target.value)}
                        placeholder="벤더"
                        style={headerInputStyle}
                        onKeyDown={(event) => event.key === "Enter" && submitSearch()}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </HeaderControl>
            ),
            width: "100px",
            textAlign: "center"
        },
        {
            no: 3,
            text: (
                <HeaderControl>
                    <input
                        value={filters.mcht_id}
                        onChange={(event) => handleFilterChange("mcht_id", event.target.value)}
                        placeholder="가맹점 ID"
                        style={headerInputStyle}
                        onKeyDown={(event) => event.key === "Enter" && submitSearch()}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </HeaderControl>
            ),
            width: "130px",
            textAlign: "center"
        },
        {
            no: 4,
            text: (
                <HeaderControl>
                    <input
                        value={filters.app_no}
                        onChange={(event) => handleFilterChange("app_no", event.target.value)}
                        placeholder="승인번호"
                        style={headerInputStyle}
                        onKeyDown={(event) => event.key === "Enter" && submitSearch()}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </HeaderControl>
            ),
            width: "130px",
            textAlign: "center"
        },
        { no: 5, text: <HeaderCell label="상태" />, width: "130px", textAlign: "center" },
        { no: 6, text: <HeaderCell label="시도" />, width: "70px", textAlign: "center" },
        { no: 7, text: <HeaderCell label="최초 전송" />, width: "150px", textAlign: "center" },
        { no: 8, text: <HeaderCell label="마지막 전송" />, width: "150px", textAlign: "center" },
        { no: 9, text: <HeaderCell label="다음 재시도" />, width: "150px", textAlign: "center" },
        { no: 10, text: <HeaderCell label="수동 작업자" />, width: "110px", textAlign: "center" },
        { no: 11, text: <HeaderCell label="수동 전송" />, width: "150px", textAlign: "center" },
        { no: 12, text: <HeaderCell label="생성일" />, width: "150px", textAlign: "center" },
        { no: 13, text: <HeaderCell label="액션" />, width: "140px", textAlign: "center" },
    ];

    const tableHeight = `min(calc(100vh - 425px), ${MAX_VISIBLE_TABLE_ROWS * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT}px)`;

    const tableBodyData = logs.map((log) => ([
        { no: 1, text: log.id ?? "-", width: "80px", textAlign: "center" },
        { no: 2, text: log.vendor ?? "-", width: "100px", textAlign: "center" },
        { no: 3, text: log.mcht_id ?? "-", width: "130px", textAlign: "left" },
        { no: 4, text: log.app_no ?? "-", width: "130px", textAlign: "left" },
        { no: 5, text: <StatusBadge status={log.status} tooltip={RESENDABLE_STATUSES.includes(log.status) ? log.last_error : ""} />, width: "130px", textAlign: "center", visible: true },
        { no: 6, text: log.attempt_count ?? 0, width: "70px", textAlign: "center" },
        { no: 7, text: formatKstDateTime(log.first_sent_at), width: "150px", textAlign: "center" },
        { no: 8, text: formatKstDateTime(log.last_sent_at), width: "150px", textAlign: "center" },
        { no: 9, text: formatKstDateTime(log.next_retry_at), width: "150px", textAlign: "center" },
        { no: 10, text: log.manual_sent_by || "-", width: "110px", textAlign: "center" },
        { no: 11, text: formatKstDateTime(log.manual_sent_at), width: "150px", textAlign: "center" },
        { no: 12, text: formatKstDateTime(log.created_at), width: "150px", textAlign: "center" },
        {
            no: 13,
            text: (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                    <button
                        className="btn"
                        style={rowButtonStyle}
                        onClick={(event) => {
                            event.stopPropagation();
                            openDetailModal(log);
                        }}
                        title="상세"
                    >
                        <Eye size={14} />
                    </button>
                    {canManualResend && RESENDABLE_STATUSES.includes(log.status) && (
                        <button
                            className="btn btn-primary"
                            style={rowButtonStyle}
                            onClick={(event) => {
                                event.stopPropagation();
                                openResendModal(log);
                            }}
                            title="수동 재전송"
                        >
                            <RefreshCw size={14} />
                        </button>
                    )}
                </div>
            ),
            width: "140px",
            textAlign: "center",
        },
    ]));

    if (loading) {
        return <LoadingModal />;
    }

    return (
        <div style={{ padding: "20px", marginTop: "52px" }}>
            <MessageModal message={message} />

            <div style={pageHeaderStyle}>
                <div>
                    <div style={titleRowStyle}>
                        <h2 style={{ fontSize: "22px", fontWeight: 700, marginBottom: 0 }}>노티 바이패스 전송 이력</h2>
                        <ManualDownloadLink
                            href="/manuals/onu-bypass-manual-a4.svg"
                            fileName="onu-bypass-manual-a4.svg"
                            tone="teal"
                        />
                        <PageHelp
                            tone="amber"
                            width="540px"
                            sections={[
                                {
                                    title: "설명",
                                    items: [
                                        "바이패스 전송 결과를 성공/실패 기준으로 확인하는 화면입니다.",
                                        "실패한 전송 건은 거래 내용을 변경하지 않고 다시 보낼 수 있습니다.",
                                    ],
                                },
                                {
                                    title: "가이드",
                                    items: [
                                        "기본은 전체 상태로 조회하고, 문제가 의심될 때 상태를 실패로 바꿔 확인합니다.",
                                        "생성일 기간을 선택한 뒤 검색을 눌러 해당 기간의 전송 이력을 조회합니다.",
                                        "실패 상태 배지에 마우스를 올리면 오류 이유를 확인할 수 있습니다.",
                                        "재전송이 필요한 실패 건은 액션의 재전송 버튼을 눌러 원본 payload 그대로 다시 전송합니다.",
                                        {
                                            text: "실패 상태 배지의 툴팁 예시입니다.",
                                            extra: (
                                                <div style={tooltipSampleStyle}>
                                                    <StatusBadge status="FAILED" tooltip="" />
                                                    <div style={tooltipMockStyle}>
                                                        <strong style={{ display: "block", marginBottom: "3px" }}>오류 이유</strong>
                                                        connect ETIMEDOUT 203.0.113.10:443
                                                    </div>
                                                </div>
                                            ),
                                        },
                                    ],
                                },
                                {
                                    title: "주의사항",
                                    items: [
                                        "재전송 전에는 거래 정보가 맞는지 확인합니다. 수동 재전송은 중복 전송 가능성이 있습니다.",
                                        ...visibleGuideMessages,
                                    ],
                                },
                            ]}
                        />
                    </div>
                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "4px" }}>바이패스 전송 이력을 성공/실패 기준으로 확인합니다.</div>
                </div>
            </div>

            <OnuManageTabs />

            <div style={filterPanelStyle}>
                <div style={filterGroupStyle}>
                    <select
                        value={filters.statuses.length ? filters.statuses.join(",") : "all"}
                        onChange={(event) => handleStatusFilterChange(event.target.value)}
                        style={{ ...filterSelectStyle, width: "180px" }}
                        title="상태"
                    >
                        <option value="all">전체 상태</option>
                        <option value={SUCCESS_STATUSES.join(",")}>성공</option>
                        <option value={FAILURE_STATUSES.join(",")}>실패</option>
                    </select>
                    <DateInput
                        value={filters.created_from}
                        onChange={(value) => handleFilterChange("created_from", value)}
                        title="생성 시작일"
                    />
                    <span style={{ color: "#94a3b8", fontWeight: 700 }}>~</span>
                    <DateInput
                        value={filters.created_to}
                        onChange={(value) => handleFilterChange("created_to", value)}
                        title="생성 종료일"
                    />
                </div>
                <div style={filterGroupStyle}>
                    <button type="button" onClick={() => setDatePreset("today")} style={quickFilterButtonStyle}>오늘</button>
                    <button type="button" onClick={() => setDatePreset("week")} style={quickFilterButtonStyle}>7일</button>
                    <button type="button" onClick={() => setDatePreset("month")} style={quickFilterButtonStyle}>30일</button>
                    <button type="button" onClick={() => setDatePreset("all")} style={quickFilterButtonStyle}>기간 전체</button>
                    <select
                        value={filters.limit}
                        onChange={(event) => handleFilterChange("limit", Number(event.target.value))}
                        style={{ ...filterSelectStyle, width: "78px" }}
                        title="조회 건수"
                    >
                        <option value={50}>50건</option>
                        <option value={100}>100건</option>
                        <option value={200}>200건</option>
                    </select>
                    <button type="button" onClick={submitSearch} style={primaryFilterButtonStyle}>
                        <Search size={14} />
                        검색
                    </button>
                    <button type="button" onClick={resetSearch} style={secondaryFilterButtonStyle}>
                        <X size={14} />
                        초기화
                    </button>
                </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", color: "#475569", fontSize: "13px" }}>
                <span>총 {total.toLocaleString()}건</span>
                <span>{page.toLocaleString()} / {totalPage.toLocaleString()} 페이지</span>
            </div>

            <List
                tableHeaderData={tableHeaderData}
                tableBodyData={tableBodyData}
                filter_tableBodyData={tableBodyData}
                page={1}
                setPage={() => { }}
                maxFirstPage={tableBodyData.length || 1}
                height={tableHeight}
                nonePage={false}
                spinner={listLoading || detailLoading}
                syncHorizontalScroll={true}
            />

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "12px" }}>
                <button
                    className="btn"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                    이전
                </button>
                <span style={{ minWidth: "90px", textAlign: "center", color: "#334155" }}>{page} / {totalPage}</span>
                <button
                    className="btn"
                    disabled={page >= totalPage || listLoading}
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPage))}
                >
                    다음
                </button>
            </div>

            {detailLog && (
                <DetailModal
                    log={detailLog}
                    canManualResend={canManualResend && RESENDABLE_STATUSES.includes(detailLog.status)}
                    onClose={() => setDetailLog(null)}
                    onCopy={copyText}
                    onResend={() => {
                        setResendTarget(detailLog);
                        setResendMemo("");
                        setResendConfirmed(false);
                        setDetailLog(null);
                    }}
                />
            )}

            {resendTarget && (
                <ResendModal
                    log={resendTarget}
                    memo={resendMemo}
                    confirmed={resendConfirmed}
                    resending={resending}
                    onChangeMemo={setResendMemo}
                    onChangeConfirmed={setResendConfirmed}
                    onClose={() => {
                        if (resending) return;
                        setResendTarget(null);
                    }}
                    onSubmit={manualResend}
                />
            )}
        </div>
    );
}

function HeaderControl({ children }) {
    return (
        <div style={{ width: "100%", minHeight: "36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {children}
        </div>
    );
}

function HeaderCell({ label }) {
    return (
        <HeaderControl>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#334155", lineHeight: 1.1 }}>{label}</span>
        </HeaderControl>
    );
}

function DateInput({ value, onChange, title }) {
    return (
        <input
            type="date"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            style={{ ...filterSelectStyle, width: "142px" }}
            title={title}
            aria-label={title}
        />
    );
}

function StatusBadge({ status, tooltip, forceTooltip = false }) {
    const [hovered, setHovered] = useState(false);
    const style = statusBadgeStyles[status] ?? statusBadgeStyles.default;
    const label = getStatusLabel(status);
    const hasTooltip = Boolean(tooltip);
    const showTooltip = hasTooltip && (hovered || forceTooltip);

    return (
        <span
            style={statusBadgeWrapperStyle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span style={{ ...style, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "4px", minWidth: "96px", minHeight: "24px", borderRadius: "999px", padding: "2px 8px", fontSize: "11px", fontWeight: 800, cursor: hasTooltip ? "help" : "default", boxShadow: hasTooltip ? "0 0 0 2px rgba(245, 158, 11, 0.18)" : undefined }}>
                {label}
                {hasTooltip && <span style={statusHintMarkStyle}>?</span>}
            </span>
            {showTooltip && (
                <span style={statusTooltipStyle}>
                    <strong style={{ display: "block", marginBottom: "3px" }}>오류 이유</strong>
                    {tooltip}
                </span>
            )}
        </span>
    );
}

function DetailModal({ log, canManualResend, onClose, onCopy, onResend }) {
    const payloadText = formatJson(log.payload_json);
    const errorText = formatJson(log.last_error);
    const responseText = formatJson(log.last_response);

    return (
        <div className="modal" tabIndex="-1" style={{ display: "block", zIndex: 100000 }}>
            <div className="modal-dialog" style={{ width: "940px", maxWidth: "96%", padding: "18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700 }}>노티 바이패스 전송 상세</h3>
                    <button className="btn" onClick={onClose} style={{ padding: "4px 8px" }}>
                        <X size={16} />
                    </button>
                </div>

                <InfoGrid
                    items={[
                        ["번호", log.id],
                        ["벤더", log.vendor],
                        ["가맹점 ID", log.mcht_id],
                        ["승인번호", log.app_no],
                        ["상태", <StatusBadge status={log.status} />],
                        ["시도 횟수", log.attempt_count ?? 0],
                        ["전송 방식", log.method],
                        ["전송 URL", log.endpoint_url],
                        ["최초 전송", formatKstDateTime(log.first_sent_at)],
                        ["마지막 전송", formatKstDateTime(log.last_sent_at)],
                        ["다음 재시도", formatKstDateTime(log.next_retry_at)],
                        ["수동 작업자", log.manual_sent_by],
                        ["수동 전송", formatKstDateTime(log.manual_sent_at)],
                        ["메모", log.memo],
                    ]}
                />

                <RawSection title="원본 payload" value={payloadText} onCopy={() => onCopy(payloadText, "원본 payload")} />
                <RawSection title="마지막 오류" value={errorText} onCopy={() => onCopy(errorText, "마지막 오류")} />
                <RawSection title="마지막 응답" value={responseText} onCopy={() => onCopy(responseText, "마지막 응답")} />

                <div className="modal-footer" style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "14px" }}>
                    <button className="btn" onClick={onClose}>닫기</button>
                    {canManualResend && (
                        <button className="btn btn-primary" onClick={onResend}>
                            <RefreshCw size={16} style={{ marginRight: "5px" }} />
                            수동 재전송
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ResendModal({ log, memo, confirmed, resending, onChangeMemo, onChangeConfirmed, onClose, onSubmit }) {
    return (
        <div className="modal" tabIndex="-1" style={{ display: "block", zIndex: 100001 }}>
            <div className="modal-dialog" style={{ width: "680px", maxWidth: "96%", padding: "18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700 }}>노티 바이패스 수동 재전송</h3>
                    <button className="btn" onClick={onClose} disabled={resending} style={{ padding: "4px 8px" }}>
                        <X size={16} />
                    </button>
                </div>

                <div style={{ border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#991b1b", borderRadius: "6px", padding: "10px 12px", marginBottom: "12px", lineHeight: 1.6, fontSize: "13px" }}>
                    <div><AlertTriangle size={15} style={{ display: "inline", marginRight: "5px" }} />원본 payload를 그대로 외부 endpoint에 다시 전송합니다.</div>
                    <div>중복 전송 가능성이 있으므로 거래 정보를 반드시 확인하세요.</div>
                </div>

                <InfoGrid
                    items={[
                        ["벤더", log.vendor],
                        ["가맹점 ID", log.mcht_id],
                        ["승인번호", log.app_no],
                        ["전송 URL", log.endpoint_url],
                        ["전송 방식", log.method],
                        ["시도 횟수", log.attempt_count ?? 0],
                        ["마지막 전송", formatKstDateTime(log.last_sent_at)],
                    ]}
                />

                <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "12px", color: "#334155", fontSize: "13px", fontWeight: 700 }}>
                    <span>작업 메모</span>
                    <textarea
                        value={memo}
                        onChange={(event) => onChangeMemo(event.target.value)}
                        style={{ ...inputStyle, minHeight: "72px", resize: "vertical" }}
                        placeholder="수동 재전송 사유를 입력하세요."
                    />
                </label>

                <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", color: "#334155", fontSize: "13px", fontWeight: 700 }}>
                    <input type="checkbox" checked={confirmed} onChange={(event) => onChangeConfirmed(event.target.checked)} />
                    거래 정보를 확인했고 수동 재전송을 진행합니다.
                </label>

                <div className="modal-footer" style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" }}>
                    <button className="btn" onClick={onClose} disabled={resending}>취소</button>
                    <button className="btn btn-primary" onClick={onSubmit} disabled={!confirmed || resending}>
                        <RefreshCw size={16} style={{ marginRight: "5px" }} />
                        재전송 실행
                    </button>
                </div>
            </div>
        </div>
    );
}

function InfoGrid({ items }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px", marginBottom: "12px" }}>
            {items.map(([label, value]) => (
                <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px", minWidth: 0 }}>
                    <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginBottom: "4px" }}>{label}</div>
                    <div title={typeof value === "string" ? value : undefined} style={{ color: "#0f172a", fontSize: "13px", wordBreak: "break-all" }}>
                        {value === null || value === undefined || value === "" ? "-" : value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function RawSection({ title, value, onCopy }) {
    return (
        <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                <h4 style={{ fontSize: "13px", fontWeight: 800, color: "#334155" }}>{title}</h4>
                <button className="btn" onClick={onCopy} style={{ padding: "4px 8px", fontSize: "12px" }}>
                    <Copy size={14} style={{ marginRight: "4px" }} />
                    복사
                </button>
            </div>
            <pre style={{ margin: 0, maxHeight: "170px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "6px", backgroundColor: "#f8fafc", color: "#0f172a", padding: "10px", fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {value}
            </pre>
        </div>
    );
}

const inputStyle = {
    width: "100%",
    minHeight: "36px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "6px 8px",
    backgroundColor: "#fff",
    fontSize: "13px",
};

const headerInputStyle = {
    width: "100%",
    height: "32px",
    border: "1px solid #94a3b8",
    borderRadius: "5px",
    padding: "4px 8px",
    backgroundColor: "#fff",
    color: "#0f172a",
    fontSize: "12px",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
};


const pageHeaderStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "14px",
};

const titleRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

const tooltipSampleStyle = {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "12px",
    padding: "10px",
    border: "1px solid #fde68a",
    borderRadius: "6px",
    backgroundColor: "#fff7ed",
};

const tooltipMockStyle = {
    width: "245px",
    maxWidth: "100%",
    padding: "8px 10px",
    borderRadius: "6px",
    backgroundColor: "#111827",
    color: "#fff",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.18)",
    fontSize: "12px",
    lineHeight: 1.5,
    textAlign: "left",
    wordBreak: "break-word",
};

const statusBadgeWrapperStyle = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
};

const statusHintMarkStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "14px",
    height: "14px",
    borderRadius: "999px",
    backgroundColor: "rgba(146, 64, 14, 0.13)",
    fontSize: "10px",
    lineHeight: 1,
};

const statusTooltipStyle = {
    position: "absolute",
    left: "calc(100% + 8px)",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 20,
    width: "260px",
    maxWidth: "70vw",
    padding: "8px 10px",
    borderRadius: "6px",
    backgroundColor: "#111827",
    color: "#fff",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.22)",
    fontSize: "12px",
    lineHeight: 1.5,
    whiteSpace: "normal",
    textAlign: "left",
    wordBreak: "break-word",
};

const filterPanelStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    borderRadius: "6px",
    padding: "10px",
    marginBottom: "10px",
};

const filterGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
};

const filterSelectStyle = {
    height: "34px",
    border: "1px solid #94a3b8",
    borderRadius: "5px",
    padding: "4px 8px",
    backgroundColor: "#fff",
    color: "#0f172a",
    fontSize: "12px",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
};

const quickFilterButtonStyle = {
    height: "34px",
    border: "1px solid #cbd5e1",
    borderRadius: "5px",
    backgroundColor: "#fff",
    color: "#334155",
    padding: "0 10px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
};

const primaryFilterButtonStyle = {
    height: "34px",
    border: "1px solid #2563eb",
    borderRadius: "5px",
    backgroundColor: "#2563eb",
    color: "#fff",
    padding: "0 12px",
    fontSize: "12px",
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
};

const secondaryFilterButtonStyle = {
    ...quickFilterButtonStyle,
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
};

const rowButtonStyle = {
    minWidth: "32px",
    height: "30px",
    padding: "4px 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};


const statusBadgeStyles = {
    FAILED: { backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" },
    MANUAL_FAILED: { backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" },
    RETRYING: { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" },
    SUCCESS: { backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" },
    MANUAL_SUCCESS: { backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" },
    PENDING: { backgroundColor: "#e0f2fe", color: "#075985", border: "1px solid #bae6fd" },
    default: { backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" },
};

export default NotiBypassSendLogs;
